import type { KeyboardEvent, MouseEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { selectTriggerSizes } from '@components/classes/select';
import Icon from '@components/Icon';
import type { InputMultiSelectProps } from '@components/InputMultiSelect/InputMultiSelect';
import { InputSelectMenuContainer, inputSelectStyles } from '@components/InputSelect';
import { MAX_DROPDOWN_HEIGHT_PX } from '@components/shared';
import { Show, ShowFirstMatching } from '@components/Show';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { VirtualizedList } from '@components/virtualization';
import type { UISelectionType } from '@root/@types/types';
import { possibleValuesForOther } from '@root/helpers/constants';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { useCombobox, useMultipleSelection } from 'downshift';
import { isString } from 'lodash-es';
import { twMerge } from 'tailwind-merge';

import { useChipOverflow } from './use-chip-overflow';

const inputClasses = cva(
  [
    'multi-select',
    'flex',
    'cursor-pointer',
    'justify-between',
    'rounded-md',
    'border',
    'border-info-300',
    'items-center',
  ],
  {
    variants: {
      size: selectTriggerSizes,
      isDisabled: { true: 'cursor-not-allowed! opacity-50' },
      isIncomplete: { true: 'field-incomplete' },
      isOther: { true: 'cursor-default!' },
    },
  },
);

// Base chip style: never wraps; long labels capped + ellipsised via CSS (max-w + the label's
// `truncate`). Consumers can override max-w via selectedItemClassName. `shrink-0` here is the
// default; display chips override it to `shrink` (see renderItems) so the "+N more" pill wins
// space on a narrow overflowing row, while the pill + measure chips stay `shrink-0`.
// max-w caps the chip but `min(...,100%)` keeps it within its (possibly narrow) wrapper so the
// remove-X is never clipped by the wrapper's overflow-x-clip — see narrow tag-editor case.
const selectedItemStyle =
  'rounded-md bg-rounded py-1 -my-1 pl-3 pr-2 mr-1 space-x-1 text-sm font-normal bg-info-200! shrink-0 whitespace-nowrap max-w-[min(240px,100%)]';

const formatItemLabel = <T = string,>(item: T, options: readonly string[] | readonly UISelectionType<T>[]): string => {
  if (isArrayOfStrings(options) && isString(item)) {
    return item;
  }
  return (options as UISelectionType<T>[]).find((option) => option.value === item)?.name ?? '';
};

type InputMultiSelectInnerProps<T = string> = Pick<
  InputMultiSelectProps<T>,
  | 'dataTestId'
  | 'dropdownInnerWrapperClassName'
  | 'dropdownWrapperClassName'
  | 'id'
  | 'isDisabled'
  | 'isIncomplete'
  | 'isSearchable'
  | 'menuOuterClassName'
  | 'multiSelectDropdownClasses'
  | 'name'
  | 'onChange'
  | 'options'
  | 'placeholder'
  | 'selectedItemClassName'
  | 'selectedItemLabelClassName'
  | 'remainingItemsClassName'
  | 'remainingTriggerClassName'
  | 'shouldRenderInsidePortal'
  | 'size'
  | 'value'
>;

const getDefaultInitialSelectedItems = (value: unknown) => (typeof value === 'string' && !!value ? [value] : []);

const isEnterKey = (event: KeyboardEvent) => event.key === 'Enter';

const isArrayOfStrings = (array: readonly unknown[]): array is string[] => array.every(isString);

export const InputMultiSelectInner = <T = string,>({
  dataTestId,
  dropdownInnerWrapperClassName = '',
  dropdownWrapperClassName = '',
  isDisabled,
  isIncomplete,
  isSearchable,
  menuOuterClassName = '',
  multiSelectDropdownClasses = '',
  name = 'multiselect',
  onChange,
  options,
  placeholder,
  selectedItemClassName = '',
  selectedItemLabelClassName = '',
  remainingItemsClassName = '',
  remainingTriggerClassName = '',
  shouldRenderInsidePortal,
  size = 'md',
  value = [],
}: InputMultiSelectInnerProps<T>) => {
  const [remainingPopoverOpen, setRemainingPopoverOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string | undefined>('');
  const { getSelectedItemProps, getDropdownProps, addSelectedItem, removeSelectedItem, selectedItems } =
    useMultipleSelection<T>({
      selectedItems: Array.isArray(value) ? value : (getDefaultInitialSelectedItems(value) as T[]),
      onSelectedItemsChange: ({ selectedItems: newSelectedItems }) => {
        if (newSelectedItems?.length) {
          const valueForOther = possibleValuesForOther.find((possibleValueForOther) =>
            newSelectedItems.includes(possibleValueForOther as T),
          );
          if (valueForOther !== undefined) {
            onChange([valueForOther as T], name);
          } else {
            onChange(newSelectedItems, name);
          }
        } else {
          onChange(undefined, name);
        }
      },
    });

  const getFilteredItems = (items: readonly string[] | readonly UISelectionType<T>[]): UISelectionType<T>[] => {
    const result = items.filter((item) => {
      if (isArrayOfStrings(selectedItems) && isString(item)) {
        return (
          selectedItems.indexOf(item) < 0 &&
          String(item ?? '')
            .toLowerCase()
            .includes(String(inputValue ?? '').toLowerCase())
        );
      }

      if (!isString(item)) {
        const isNotFound = !selectedItems.some((val) => val === item.value);

        return (
          isNotFound &&
          String(item.name ?? '')
            .toLowerCase()
            .includes(String(inputValue ?? '').toLowerCase())
        );
      }

      return true;
    });

    return result.map((item) => (isString(item) ? { name: item, value: item } : item)) as UISelectionType<T>[];
  };

  const anchorRef = useRef<HTMLButtonElement>(null);

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectItem,
    toggleMenu,
    closeMenu,
  } = useCombobox<UISelectionType<T> | null>({
    items: getFilteredItems(options),
    stateReducer: (_state, { type, changes }) => {
      // A surrounding dialog's focus trap can yank focus back to the trigger for one tick while the
      // portalled menu's FocusScope is still mounting; that transient blur must not close the menu.
      if (
        shouldRenderInsidePortal &&
        type === useCombobox.stateChangeTypes.InputBlur &&
        anchorRef.current?.contains(document.activeElement)
      ) {
        return { ...changes, isOpen: true };
      }
      return changes;
    },
    onStateChange: ({ inputValue, type, selectedItem }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(inputValue);
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          if (selectedItem?.value) {
            setInputValue('');
            addSelectedItem(selectedItem.value);
            selectItem(null);
          }

          break;
        default:
          break;
      }
    },
  });

  const isOther = useMemo(
    () =>
      isArrayOfStrings(selectedItems) &&
      selectedItems?.some((selectedValue: string) => possibleValuesForOther.includes(selectedValue)),
    [selectedItems],
  );

  const handleRemoveSelectedItem = (selectedItem: T) => {
    removeSelectedItem(selectedItem);
  };

  const isFull = getFilteredItems(options).length === 0;
  const isDropdownOpen = isOpen && !isOther;

  const handleRemoveItem = (event: MouseEvent<HTMLSpanElement> | KeyboardEvent<HTMLSpanElement>, selectedItem: T) => {
    if (!isDisabled) {
      event.stopPropagation();
      handleRemoveSelectedItem(selectedItem);
      closeMenu();
    }
  };

  const handlePopoverTriggerClick = (e: MouseEvent) => e.stopPropagation();

  const handlePopoverTriggerKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
  };

  const shouldVirtualize = options.length > 100;

  const measureKey = selectedItems.map((i) => formatItemLabel(i, options)).join('|');
  const { containerRef, measureRef, visibleCount } = useChipOverflow(selectedItems.length, measureKey, true);

  const effectiveMax = visibleCount;
  const displayItems = selectedItems.slice(0, effectiveMax);
  const remainingItems = selectedItems.slice(effectiveMax);
  const remainingCount = remainingItems.length;

  useEffect(() => {
    if (remainingCount === 0) {
      setRemainingPopoverOpen(false);
    }
  }, [remainingCount]);

  const remainingRowWrapperClass =
    remainingItemsClassName !== ''
      ? twMerge('flex items-center mb-0 min-w-0 max-w-full', remainingItemsClassName)
      : twMerge(selectedItemStyle, 'flex items-center mb-0 min-w-0 max-w-full');

  const renderItems = (items: T[], itemClassName = '', labelClassName = '', isRemainingRow = false) => (
    <>
      {items.map((selectedItem) => (
        <div
          className={
            isRemainingRow
              ? remainingRowWrapperClass
              : twMerge(
                  clsx(selectedItemStyle, 'flex min-w-0 shrink items-center'),
                  clsx(selectedItemClassName, itemClassName),
                )
          }
          key={`selected-item-${selectedItem}`}
        >
          <span
            {...getSelectedItemProps({ selectedItem, index: selectedItems.indexOf(selectedItem) })}
            title={formatItemLabel(selectedItem, options)}
            className={clsx('min-w-0 truncate', labelClassName)}
          >
            {formatItemLabel(selectedItem, options)}
          </span>
          <span
            data-testid={
              dataTestId
                ? `${dataTestId}:remove-item-${formatItemLabel(selectedItem, options)}`
                : `remove-item-${formatItemLabel(selectedItem, options)}`
            }
            className={clsx(
              'inline-flex size-5 shrink-0 items-center justify-center rounded-full p-0 text-xs leading-none',
              isDisabled ? 'cursor-not-allowed' : 'hover:bg-info-400 cursor-pointer',
            )}
            onClick={(ev) => handleRemoveItem(ev, selectedItem)}
            onKeyDown={(ev) => (isEnterKey(ev) ? handleRemoveItem(ev, selectedItem) : undefined)}
          >
            &#10005;
          </span>
        </div>
      ))}
    </>
  );

  return (
    <div className={clsx('relative', dropdownWrapperClassName)}>
      <button
        type="button"
        data-testid={`${dataTestId}:select-trigger`}
        className={clsx(
          inputClasses({
            isDisabled,
            isIncomplete,
            isOther,
            size,
          }),
          'w-full bg-transparent text-left',
          multiSelectDropdownClasses,
        )}
        {...getToggleButtonProps({
          disabled: isDisabled,
          onKeyDown: (e) => {
            if (isDisabled) return;
            if (e.key === 'ArrowDown' && !isOpen) {
              e.preventDefault();
              toggleMenu();
            }
          },
        })}
        tabIndex={isDisabled ? -1 : 0}
        ref={anchorRef}
      >
        <div
          ref={containerRef}
          className={clsx(
            // flex-nowrap forces a single row so the chip-overflow measurement (always single-row)
            // matches layout — product requires single-row + "+N more" for all multiselects.
            'multi-select-wrapper flex flex-nowrap items-center',
            'relative min-w-0 flex-1 overflow-x-clip',
            dropdownInnerWrapperClassName,
          )}
        >
          <div
            ref={measureRef}
            aria-hidden
            className="pointer-events-none invisible absolute top-0 left-0 flex flex-nowrap items-center"
          >
            {selectedItems.map((selectedItem) => (
              <div
                key={`measure-${selectedItem}`}
                className={twMerge(clsx(selectedItemStyle, 'flex items-center'), selectedItemClassName)}
              >
                <span className={clsx('min-w-0 truncate', selectedItemLabelClassName)}>
                  {formatItemLabel(selectedItem, options)}
                </span>
                <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full p-0 text-xs leading-none">
                  &#10005;
                </span>
              </div>
            ))}
          </div>
          {renderItems(displayItems, '', selectedItemLabelClassName)}
          <Show when={remainingCount > 0}>
            <Popover open={remainingPopoverOpen} onOpenChange={setRemainingPopoverOpen}>
              <PopoverTrigger asChild>
                <div
                  data-testid={dataTestId ? `${dataTestId}:more` : 'more'}
                  className={twMerge(
                    clsx(selectedItemStyle, 'flex items-center'),
                    selectedItemClassName,
                    remainingTriggerClassName,
                    'grow-0',
                  )}
                  onClick={handlePopoverTriggerClick}
                  onKeyDown={handlePopoverTriggerKeyDown}
                >
                  <span className={clsx(selectedItemLabelClassName, 'text-info-500')}>+{remainingCount} more</span>
                </div>
              </PopoverTrigger>
              <PopoverContent className="z-1200 text-gray-900">
                <div className="flex max-h-96 flex-col items-start gap-3 overflow-y-auto pt-1">
                  {renderItems(remainingItems, 'mb-0', clsx(selectedItemLabelClassName, 'flex-1'), true)}
                </div>
              </PopoverContent>
            </Popover>
          </Show>
          {selectedItems.length === 0 && (
            <span {...getLabelProps()} className="text-info-400 text-sm font-normal">
              {placeholder}
            </span>
          )}
        </div>
        <div
          data-testid={dataTestId ? `${dataTestId}:toggle` : 'toggle'}
          className={clsx('flex', isOther && 'cursor-not-allowed')}
        >
          {!isDropdownOpen && <Icon name="chevron-down" className="text-info-800 ml-2 self-center fill-current" />}
          {isDropdownOpen && <Icon name="chevron-up" className="text-primary-700 ml-2 self-center fill-current" />}
        </div>
      </button>
      {(!isDropdownOpen || !isSearchable) && (
        <input className="hidden" {...getInputProps(getDropdownProps({ preventKeyAction: isDropdownOpen }))} />
      )}
      <div {...getMenuProps()}>
        {isDropdownOpen && (
          <InputSelectMenuContainer
            anchorRef={anchorRef}
            className={clsx(inputSelectStyles.menuOuter, menuOuterClassName)}
            offsetY={4}
            shouldRenderInsidePortal={shouldRenderInsidePortal}
          >
            <div className="flex min-h-0 flex-col">
              {isSearchable && (
                <input
                  className={inputSelectStyles.input}
                  placeholder="Search..."
                  {...getInputProps({}, { suppressRefError: shouldRenderInsidePortal })}
                />
              )}
              <ShowFirstMatching>
                <Show when={shouldVirtualize}>
                  <VirtualizedList<UISelectionType<T>>
                    data={getFilteredItems(options)}
                    itemHeight={36}
                    height={MAX_DROPDOWN_HEIGHT_PX}
                    renderItem={(item) => (
                      <li
                        {...getItemProps({ item, index: item.index })}
                        data-testid={dataTestId ? `${dataTestId}:option-${item.name}` : `option-${item.name}`}
                        className={clsx(
                          inputSelectStyles.menuItem,
                          item.index === highlightedIndex ? 'bg-info-100!' : 'hover:bg-info-50! bg-white!',
                        )}
                        key={item.value}
                      >
                        <span className="truncate">{item.name}</span>
                      </li>
                    )}
                  />
                  <Show when={isFull}>
                    <li className={clsx(inputSelectStyles.menuItem, 'bg-white! italic opacity-60')}>No options left</li>
                  </Show>
                </Show>
                <Show when={!shouldVirtualize}>
                  <ul
                    className={inputSelectStyles.menuInner}
                    style={{
                      maxHeight: MAX_DROPDOWN_HEIGHT_PX,
                    }}
                  >
                    {getFilteredItems(options).map((item, index) => (
                      <li
                        data-testid={dataTestId ? `${dataTestId}:option-${item.name}` : `option-${item.name}`}
                        className={clsx(
                          inputSelectStyles.menuItem,
                          index === highlightedIndex ? 'bg-info-100!' : 'hover:bg-info-50! bg-white!',
                        )}
                        {...getItemProps({ item, index })}
                        key={`${item.value}`}
                      >
                        <span className="truncate">{item.name}</span>
                      </li>
                    ))}
                    <Show when={isFull}>
                      <li className={clsx(inputSelectStyles.menuItem, 'bg-white! italic opacity-60')}>
                        No options left
                      </li>
                    </Show>
                  </ul>
                </Show>
              </ShowFirstMatching>
            </div>
          </InputSelectMenuContainer>
        )}
      </div>
    </div>
  );
};
